import React from "react";
import { timeAgoInWordsLong, prettyDate } from "@shared/lib/time";
import SlidingToggleSwitch from "@ats/src/components/forms/SlidingToggleSwitch";
import { OptionPair } from "@ats/src/components/forms/SlidingToggleSwitch";

import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import Icon from "@ats/src/components/shared/Icon";
import Button from "@ats/src/components/shared/Button";

import ContactUsCallout from "@ats/src/views/accountAdmin/accountBilling/ContactUsCallout";
import AiCreditsCallout from "@ats/src/views/accountAdmin/accountBilling/AiCreditsCallout";
import { FeatureFlipper } from "@ats/src/components/shared/FeatureFlipper";

import {
  Subtitle,
  PricingOptions,
  Badge,
  Promo,
  CurrentSubscription,
  BillingPeriodToggleWrapper,
  PricingOptionsContainer,
} from "@ats/src/views/accountAdmin/accountBilling/AccountBillingComponents";

import {
  useCreateStripeCustomerPortalSession,
  useChangeSubscriptionViaStripePortal,
  useUpdateWithPaymentMethod,
  useStripeCustomerSubscription,
} from "@shared/queryHooks/useBilling";

import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";

import PlanCard from "@ats/src/views/accountAdmin/accountBilling/PlanCard";
import { getPlansForPeriod, getPlanButtonText, getPlanButtonType } from "@ats/src/lib/planLookups";
import { usePlanLimitsGate } from "@shared/hooks/usePlanLimitsGate";
import { getPricingDisplayName, legacyPlanType, isLegacyPlan } from "@shared/lib/planHelpers";
// Updated to use new modal that handles both job and user limits
import PlanChangeBlockedModal from "@ats/src/components/modals/PlanChangeBlockedModal";
import { trackEvent } from "@shared/lib/posthog";

const AccountBillingPlans: React.FC<{
  currentOrganization: any;
  hasActiveSubscription: boolean;
  billingPrices: any[];
  history: any;
}> = ({ currentOrganization, hasActiveSubscription, billingPrices, history }) => {
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();

  /* Plan Limits Gate Hook
  --===================================================-- */
  const { checkPlanLimitsGate } = usePlanLimitsGate();

  /* Stripe Customer Subscription
  --===================================================-- */
  const {
    data: stripeCustomerSubscriptionData,
    isFetching: isFetchingStripeCustomerSubscription,
  } = useStripeCustomerSubscription({
    refetchOnWindowFocus: false,
  });
  const currentSubscription = stripeCustomerSubscriptionData
    ? stripeCustomerSubscriptionData.subscription
    : null;

  // Calculate initial billing period based on current subscription
  const currentPriceObject = currentSubscription && currentSubscription.items.data[0].price;
  const currentPlanLookupKey = currentPriceObject?.lookupKey;
  const currentPlanBillingInterval =
    currentPriceObject?.interval || currentPriceObject?.recurring?.interval;
  const currentPlanBillingPeriod: "monthly" | "yearly" =
    currentPlanBillingInterval === "year" || currentPlanLookupKey?.includes("free")
      ? "yearly"
      : currentPlanBillingInterval === "month"
      ? "monthly"
      : "yearly"; // default to yearly

  const currentLegacyPlanType = legacyPlanType(currentOrganization?.plan);
  const isOnLegacyPlan = isLegacyPlan(currentOrganization?.plan);

  const billingOptions: OptionPair = [
    {
      label: "Monthly",
      value: "monthly",
    },
    {
      label: "Yearly",
      value: "yearly",
    },
  ];
  const [billingPeriod, setBillingPeriod] = React.useState<"monthly" | "yearly">("yearly");

  // Set the correct initial billing period when subscription data loads
  React.useEffect(() => {
    if (currentSubscription && currentPriceObject) {
      const calculatedBillingPeriod: "monthly" | "yearly" = isOnLegacyPlan
        ? "yearly"
        : currentPlanBillingPeriod;

      setBillingPeriod(calculatedBillingPeriod);

      window.logger("%c[AccountBillingPlans] Setting initial billing period", "color: #1976D2", {
        calculatedBillingPeriod,
        isOnLegacyPlan,
        currentPlanBillingPeriod,
        currentPlanBillingInterval,
      });
    }
  }, [
    currentSubscription,
    currentPriceObject,
    isOnLegacyPlan,
    currentPlanBillingPeriod,
    currentPlanBillingInterval,
  ]);

  /* Create Stripe Customer Portal Session
  --===================================================-- */
  const { mutate: createStripeCustomerPortalSession } = useCreateStripeCustomerPortalSession();

  /* Create Custom Stripe Customer Portal Session for Changing Subscription
  --===================================================-- */
  const {
    mutate: changeSubscriptionViaStripePortal,
    isLoading: isLoadingChangeSubscriptionViaStripePortal,
  } = useChangeSubscriptionViaStripePortal();

  /* Update Payment Method and Change Subscription
  --===================================================-- */
  const {
    mutate: updateWithPaymentMethod,
    isLoading: isLoadingUpdateWithPaymentMethod,
  } = useUpdateWithPaymentMethod();

  // currentPriceObject is already calculated above for initial billing period
  const currentSubscriptionItemId = currentSubscription && currentSubscription.items.data[0].id;
  const currentProductPrice =
    currentPriceObject != undefined
      ? currentPriceObject.billingScheme === "tiered"
        ? currentPriceObject.tiers[0].unitAmount / 100.0
        : currentPriceObject.unitAmount / 100.0
      : null;

  window.logger("%c[AccountBillingPlans] PlanInformation", "color: #1976D2", {
    hasActiveSubscription,
    isOnLegacyPlan,
    notActiveSubscription: !hasActiveSubscription,
    notLegacyPlan: !isOnLegacyPlan,
    currentPlanBillingPeriod,
    currentPlanLookupKey,
    currentPlanBillingInterval,
  });


  // Get current plan display name
  const currentPlanDisplayName = getPricingDisplayName(currentOrganization.plan);

  const isTrialing =
    currentSubscription &&
    currentSubscription.status === "trialing" &&
    currentSubscription.discount != undefined;
  // const trialEndDays = isTrialing && formatMDY(currentSubscription.trialEnd);
  const trialEndDays = isTrialing && timeAgoInWordsLong(currentSubscription.trialEnd);

  const hasCoupon =
    currentSubscription &&
    currentSubscription.discount &&
    currentSubscription.discount.coupon != undefined;
  const discount = hasCoupon && currentSubscription.discount;
  const coupon = discount && discount.coupon;

  // Billing Prices & Plan Features
  //
  // Get plans for current billing period
  const plans = getPlansForPeriod(billingPeriod, billingPrices);
  // determine button text and type for each plan
  const plansWithButtonText = plans
    .filter((plan) => plan.key !== "free")
    .map((plan) => {
    const buttonText = currentPriceObject?.lookupKey
      ? getPlanButtonText(currentPriceObject.lookupKey, plan.lookupKey, billingPeriod)
      : isOnLegacyPlan && currentLegacyPlanType === "free"
      ? "Upgrade"
      : "Change plan";

    const buttonType = getPlanButtonType(buttonText);

    return {
      ...plan,
      buttonText,
      buttonType,
    };
  });

  window.logger("%c[AccountBillingPlans] render", "background: #ed79ed; color: #b5d0eb", {
    location,
    currentOrganization,
    currentSubscription,
    currentPriceObject,
    currentProductPrice,
    isTrialing,
    trialEndDays,
    hasCoupon,
    discount,
    coupon,
    billingPrices,
    isLoadingChangeSubscriptionViaStripePortal,
    isOnLegacyPlan,
  });

  /**
   * USED IF A CUSTOMER WANTS TO MANAGE THEIR BILLING USING THE STRIPE CUSTOMER PORTAL
   * This function handles the creation of a Stripe Customer Portal Session.
   * It calls the `createStripeCustomerPortalSession` function with specific parameters,
   * including the return URL. Upon successful creation of the session, it redirects the user
   * to the Stripe Customer Portal page. If the redirection fails, it logs the error message.
   */
  const handleCreateBillingPortalSession = () => {
    createStripeCustomerPortalSession(
      { returnUrl: "/hire/settings/billing" },
      {
        onSuccess: (data) => {
          window.logger("%c[AccountContainer] ", "color: #1976D2", { data });
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to access billing portal.";
          window.logger(
            "%c[AccountBilling] ERROR creating Custom Stripe Billing Portal session",
            "background: orange; color: white",
            error,
          );
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  /**
   * USED IF A CUSTOMER NEEDS TO UPDATE PAYMENT METHOD THEN CHANGE SUBSCRIPTION
   */
  const handleUpdateWithPaymentMethod = ({
    priceId,
    subscriptionItemId,
  }: {
    priceId: string;
    subscriptionItemId: string;
  }) => {
    updateWithPaymentMethod(
      {
        priceId,
        subscriptionItemId,
        returnUrl: "/hire/settings/billing",
      },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[AccountBilling] completed update payment method and change subscription",
            "background: orange; color: #1976D2",
            data,
          );
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] ||
            "Unable to update payment method and change subscription.";
          window.logger(
            "%c[AccountBilling] ERROR updating payment method and changing subscription",
            "background: orange; color: white",
            error,
          );
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  /**
   * USED IF A CUSTOMER WANTS TO CHANGE THEIR SUBSCRIPTION
   */
  const handleChangeSubscriptionViaStripePortal = ({
    priceId,
    subscriptionItemId,
  }: {
    priceId: string;
    subscriptionItemId: string;
  }) => {
    changeSubscriptionViaStripePortal(
      {
        priceId,
        subscriptionItemId,
        returnUrl: "/hire/settings/billing",
      },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[AccountBilling] created session for updating Subscription",
            "background: orange; color: #1976D2",
            data,
          );
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Error while opening the Stripe portal.";
          window.logger(
            "%c[AccountBilling] ERROR creating Custom Stripe Billing Portal session",
            "background: orange; color: white",
            error,
          );
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  /**
   * WRAPPER FOR SUBSCRIPTION CHANGES WITH PLAN LIMITS GATE AND PAYMENT METHOD CHECK
   */
  const handleChangeSubscriptionWithGate = (plan) => {
    const result = checkPlanLimitsGate(plan.lookupKey);

    if (result.shouldProceed) {
      if (currentOrganization.stripeDefaultPaymentMethodOnFile) {
        handleChangeSubscriptionViaStripePortal({
          priceId: plan.priceId,
          subscriptionItemId: currentSubscriptionItemId,
        });
      } else {
        handleUpdateWithPaymentMethod({
          priceId: plan.priceId,
          subscriptionItemId: currentSubscriptionItemId,
        });
      }
    } else {
      // Show plan limits modal
      trackEvent("plan_change_blocked_modal_shown", {  current_plan_lookup_key: currentPlanLookupKey, target_plan_lookup_key: plan.lookupKey });
      const modal = (
        <PlanChangeBlockedModal
          onCancel={() => removeModal()}
          modalData={result.modalData!}
          location={{ pathname: "/hire/settings/billing" }}
          history={history}
        />
      );
      openModal(modal);
    }
  };

  if (isFetchingStripeCustomerSubscription) {
    return <LoadingIndicator label="Loading..." />;
  }

  return (
    <>
      <Subtitle>Subscription options</Subtitle>
      <BillingPeriodToggleWrapper>
        <SlidingToggleSwitch
          name="billingPeriod"
          value={billingPeriod}
          options={billingOptions}
          onChange={(_, v) => setBillingPeriod(v as "monthly" | "yearly")}
        />
        <Badge style={{ marginTop: "16px" }}>2 months free</Badge>
      </BillingPeriodToggleWrapper>

      <PricingOptionsContainer>
        {currentSubscription?.status === "trialing" && (
          <CurrentSubscription>
            <p>
              <Icon name="check" />
              <span>
                You are currently on a free trial which will end on {prettyDate(currentSubscription.trialEnd)}.
              </span>
            </p>
            <Button styleType="text" onClick={handleCreateBillingPortalSession}>
              Manage billing
            </Button>
          </CurrentSubscription>
        )}

        {currentSubscription?.cancelAtPeriodEnd && (
          <CurrentSubscription>
            <p>
              <Icon name="info" />
              <span>
                Your subscription is scheduled to be canceled on {prettyDate(currentSubscription.cancelAt)}.
              </span>
            </p>
            <Button styleType="text" onClick={handleCreateBillingPortalSession}>
              Manage billing
            </Button>
          </CurrentSubscription>
        )}

        {isOnLegacyPlan && (
          <CurrentSubscription>
            <p>
              <Icon name="check" />
              <span>
                You're currently subscribed to{" "}
                {currentLegacyPlanType === "perJob"
                  ? "a legacy per-job pricing plan."
                  : currentLegacyPlanType === "unlimited"
                  ? `a legacy unlimited jobs ${
                      currentPriceObject?.interval || currentPriceObject?.recurring?.interval
                    }ly plan.`
                  : currentLegacyPlanType === "free"
                  ? "a legacy free plan."
                  : ` the ${currentPlanDisplayName} ${
                      currentPriceObject?.interval || currentPriceObject?.recurring?.interval
                    }ly pricing plan`}{" "}
                {isTrialing && !hasCoupon && `Free for ${trialEndDays}.`}
              </span>
            </p>
            <Button styleType="text" onClick={handleCreateBillingPortalSession}>
              Manage billing
            </Button>
          </CurrentSubscription>
        )}

        {hasCoupon ? (
          <Promo>
            {/* <h4>{coupon.name}</h4> */}
            <h4>Promo code applied</h4>
            <span>
              {coupon.percentOff}% off until {prettyDate(discount.end)}
            </span>
          </Promo>
        ) : null}

        <PricingOptions>
          {plansWithButtonText.map((plan) => {
            const isCurrentPlan = currentSubscription?.plan?.id === plan.priceId;

            return (
              <PlanCard
                key={plan.key}
                plan={plan}
                billingPeriod={billingPeriod}
                isCurrentPlan={isCurrentPlan}
                currentPlanLookupKey={currentPlanLookupKey}
                currentPlanBillingPeriod={currentPlanBillingPeriod}
                hasActiveSubscription={hasActiveSubscription}
                hasCoupon={hasCoupon}
                stripePromoCode={currentOrganization.stripePromoCode}
                subscriptionItemId={currentSubscriptionItemId}
                isLoading={
                  isLoadingChangeSubscriptionViaStripePortal || isLoadingUpdateWithPaymentMethod
                }
                isLoadingButton={isFetchingStripeCustomerSubscription}
                onChangeSubscription={handleChangeSubscriptionWithGate}
              />
            );
          })}
        </PricingOptions>

        <ContactUsCallout />

        <FeatureFlipper feature="AI_APPLICANT_SUMMARY">
          <AiCreditsCallout
            onClickManage={() => history.push("/hire/settings/plato-ai/billing")}
          />
        </FeatureFlipper>
      </PricingOptionsContainer>
    </>
  );
};

export default AccountBillingPlans;
